import React from "react";
import { Route, Switch } from "react-router-dom";
import Callback from "./components/Callback";
import PrivateRoute from "./components/PrivateRoute";
import HomePage, { HomeStates } from "./pages/HomePage";
import VerifyEmail from "./components/VerifyEmail";
import { Roles } from "./constants/Roles";
import { Routes } from "./constants/Routes";
import Video from "./components/Video";
import TrainingPreview from "./components/Training/TrainingPreview";
import AssignmentCreate from "./components/Assignment/AssignmentCreate";
import AssignmentPlayer from "./components/Assignment/AssignmentPlayer";
import AdminCreatePacket from "./components/Admin/AdminCreatePacket";
import Packet from "./components/Packet/Packet";
import DocumentViewPage from "./pages/DocumentViewPage";
import AdminSeedDefaults from "./components/Admin/AdminSeedDefaults";
import DashboardEmpty from "./components/DashboardEmpty";
import AdminGroupDetailsPage from "./components/Admin/AdminGroupDetails/AdminGroupDetailsPage";

const TrainingDetailsPage = React.lazy(
  () => import("./components/TrainingDetails/TrainingDetailsPage")
);

const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const Companies = React.lazy(() => import("./components/Companies"));
const CompanyCreatePage = React.lazy(() => import("./pages/CompanyCreatePage"));
const AdminEmployees = React.lazy(
  () => import("./components/Admin/AdminEmployees")
);
const Employee = React.lazy(() => import("./components/Employee"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const ContactUsPage = React.lazy(() => import("./pages/ContactUsPage"));
const CompanyContactPage = React.lazy(
  () => import("./components/CompanyContact/CompanyContactPage")
);
const ResourceLibraryPage = React.lazy(
  () => import("./pages/ResourceLibraryPage")
);
const ReportsPage = React.lazy(
  () => import("./components/Reports/ReportsPage")
);
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const TosPage = React.lazy(() => import("./pages/TosPage"));
const TrainingCreatePage = React.lazy(
  () => import("./pages/TrainingCreatePage")
);

export default function AppRoutes() {
  const adminRoles = [Roles.Admin, Roles.SuperAdmin, Roles.Tenant];
  const adminHrRoles = [
    Roles.Admin,
    Roles.SuperAdmin,
    Roles.Tenant,
    Roles.HR,
    Roles.Clerical,
  ];
  const allowSignUp = process.env.REACT_APP_ALLOW_SIGN_UP;

  return (
    <Switch>
      <Route
        exact
        path="/login"
        render={(props) => (
          <HomePage
            initialHomeState={HomeStates.Login}
            {...props}
            loadingUser={false}
          />
        )}
      />
      <PrivateRoute exact path="/" component={DashboardPage} />
      <PrivateRoute exact path="/dash" component={DashboardEmpty} />
      <Route path="/callback" component={Callback} />
      <Route path="/resource-library" component={ResourceLibraryPage} />
      <PrivateRoute
        path="/reports"
        component={ReportsPage}
        roles={[Roles.Admin, Roles.Clerical]}
      />
      <Route path="/contact-us" component={ContactUsPage} />
      <Route
        exact
        path="/loading-user"
        render={(props) => (
          <HomePage
            initialHomeState={HomeStates.Login}
            {...props}
            loadingUser={true}
          />
        )}
      />
      {allowSignUp === "true" ? (
        <Route
          exact
          path="/create"
          render={(props) => (
            <HomePage
              initialHomeState={HomeStates.Create}
              {...props}
              loadingUser={false}
            />
          )}
        />
      ) : null}

      <Route
        exact
        path="/forgot-password"
        render={(props) => (
          <HomePage
            initialHomeState={HomeStates.ForgotPassword}
            {...props}
            loadingUser={false}
          />
        )}
      />
      <Route
        exact
        path="/sso"
        render={(props) => (
          <HomePage
            initialHomeState={HomeStates.SSO}
            {...props}
            loadingUser={false}
          />
        )}
      />
      <Route exact path="/verify" component={VerifyEmail} />
      <PrivateRoute exact path={Routes.profile} component={ProfilePage} />
      <PrivateRoute
        exact
        path="/profile/:employeeId"
        component={ProfilePage}
        roles={adminHrRoles}
      />
      <PrivateRoute path="/companies/all" component={Companies} />
      <PrivateRoute
        component={CompanyCreatePage}
        path={Routes.adminCompany}
        roles={adminRoles}
      />
      <PrivateRoute path="/employees/all" component={AdminEmployees} />
      <PrivateRoute path="/employees/create" component={Employee} />
      <PrivateRoute path="/employees/:id" component={Employee} />
      <PrivateRoute path="/video" component={Video} />
      <PrivateRoute path="/terms-of-service" component={TosPage} />
      <PrivateRoute path="/admin" component={AdminPage} roles={adminHrRoles} />
      <PrivateRoute
        exact
        path="/training/create"
        component={TrainingCreatePage}
        roles={adminHrRoles}
      />
      <PrivateRoute
        exact
        path="/training/create/:trainingId/:trainingVersion"
        component={TrainingCreatePage}
        roles={adminHrRoles}
      />
      <PrivateRoute
        exact
        path="/training/view/:trainingId/:trainingVersion"
        component={TrainingPreview}
      />
      <PrivateRoute
        exact
        path="/training/details/:trainingId/:trainingVersion"
        component={TrainingDetailsPage}
        roles={adminHrRoles}
      />
      <PrivateRoute
        exact
        path="/assignment/create"
        component={AssignmentCreate}
        roles={adminHrRoles}
      />
      <PrivateRoute
        exact
        path="/assignment/:assignmentId"
        component={AssignmentPlayer}
      />
      <PrivateRoute
        exact
        path="/document/:documentId"
        component={DocumentViewPage}
      />
      <PrivateRoute exact path="/packet/create" component={AdminCreatePacket} />
      <PrivateRoute
        exact
        path="/packet/edit/:packetId"
        component={AdminCreatePacket}
      />
      <PrivateRoute exact path="/packet/:packetId" component={Packet} />
      <PrivateRoute exact path="/seed" component={AdminSeedDefaults} />
      <PrivateRoute
        exact
        path="/company-contacts"
        component={CompanyContactPage}
      />
      <PrivateRoute
        exact
        path={`/group/:groupId`}
        component={AdminGroupDetailsPage}
        roles={[Roles.Admin, Roles.Clerical]}
      />
    </Switch>
  );
}
